import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import useOutsideClick from "../hooks/useOutsideClick";
import { useLogout } from "../apiCalls/userApiCalls";
import { Container, Button, Logo } from "./ui";
import { SparkleIcon, MenuIcon, CloseIcon } from "./ui/icons";

// 72px, white, hairline bottom border, sticky (design system §13).
const NAV_LINKS = [
  { to: "/rankings", label: "Rankings" },
  { to: "/searchTournaments", label: "Tournaments" },
  { to: "/clubs", label: "Clubs" },
  { to: "/how-rankings-work", label: "How Rankings Work" },
];

const linkClass = ({ isActive }) =>
  `relative py-2 text-sm font-semibold transition-colors ${
    isActive
      ? "text-primary after:absolute after:inset-x-0 after:-bottom-[19px] after:h-0.5 after:bg-primary"
      : "text-navy hover:text-primary"
  }`;

const Navbar = () => {
  const { handleLogout: logoutUser } = useLogout();
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const userMenuRef = useRef();

  useEffect(() => {
    const loggedInUser = localStorage.getItem("user");
    if (loggedInUser) setUser(JSON.parse(loggedInUser));
  }, []);

  useOutsideClick(userMenuRef, () => {
    if (showUserMenu) setShowUserMenu(false);
  });

  const handleLogout = () => {
    logoutUser();
    setUser(null);
    setShowUserMenu(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-surface/95 backdrop-blur">
      <Container>
        <div className="flex h-18 items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <Logo />
            <nav className="hidden items-center gap-6 xl:flex">
              {NAV_LINKS.map((link) => (
                <NavLink key={link.to} to={link.to} className={linkClass}>
                  {link.label}
                </NavLink>
              ))}
              {/* Ask AI is distinguished, but stays subordinate to rankings (§14). */}
              <NavLink to="/ask" className={linkClass}>
                {({ isActive }) => (
                  <span className="inline-flex items-center gap-1.5">
                    <SparkleIcon
                      className={`h-4 w-4 ${isActive ? "text-primary" : "text-primary/70"}`}
                    />
                    Ask PadelRank AI
                  </span>
                )}
              </NavLink>
            </nav>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  aria-label="Account menu"
                  className="block rounded-full ring-2 ring-border transition hover:ring-primary/40"
                >
                  <img
                    src={
                      user?.player?.imgUrl ||
                      `${process.env.PUBLIC_URL}/default-user.jpg`
                    }
                    alt=""
                    className="h-9 w-9 rounded-full object-cover"
                  />
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 top-12 w-44 overflow-hidden rounded-md border border-border bg-surface py-1 shadow-card">
                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      className="block px-4 py-2 text-sm font-medium text-navy hover:bg-bg"
                    >
                      My profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full px-4 py-2 text-left text-sm font-medium text-danger hover:bg-bg"
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Button to="/login" variant="ghost" size="sm" className="hidden sm:inline-flex">
                  Sign In
                </Button>
                <Button to="/register" size="sm">
                  Get Ranked
                </Button>
              </>
            )}

            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation"
              aria-expanded={mobileOpen}
              className="rounded-sm p-2 text-navy transition-colors hover:bg-bg xl:hidden"
            >
              {mobileOpen ? <MenuIcon className="hidden" /> : null}
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
      </Container>

      {mobileOpen && (
        <nav className="border-t border-border bg-surface xl:hidden">
          <Container className="flex flex-col py-2">
            {[...NAV_LINKS, { to: "/ask", label: "Ask PadelRank AI" }].map(
              (link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `border-b border-border py-3 text-sm font-semibold last:border-0 ${
                      isActive ? "text-primary" : "text-navy"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              )
            )}
          </Container>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
